import { queryKeys } from '@qse/api'
import { EmptyState, useProgressTrackerAdapter } from '@qse/ui'
import { initiateFolderTreeWithTracking } from '@qse/ui/src/hooks'
import { useQueryClient } from '@tanstack/react-query'
import React, { useRef, useState } from 'react'
import { toast } from 'sonner'

import { showMultiFolderModal } from '../components/MultiFolderUploadModal'

// Utility function to check if a file should be filtered out (hidden or system files)
const shouldFilterFile = (fileName: string): boolean => {
  const isHiddenFile = fileName.startsWith('.')
  const isSystemFile = fileName === 'Thumbs.db' || fileName === 'desktop.ini'

  return isHiddenFile || isSystemFile
}

type AnyEntry = FileSystemEntry & {
  isDirectory: boolean
  isFile: boolean
  createReader?: () => FileSystemDirectoryReader
  file?: (cb: (f: File) => void, err?: (e: any) => void) => void
}

function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const out: FileSystemEntry[] = []
    const step = () => {
      reader.readEntries(
        batch => {
          if (!batch.length) return resolve(out)
          out.push(...batch)
          step()
        },
        err => reject(err),
      )
    }

    step()
  })
}

// Configuration for folder upload limits
const FOLDER_UPLOAD_LIMITS = {
  MAX_FILES: 5000, // Maximum number of files to process
  MAX_DEPTH: 10, // Maximum directory depth
  SKIP_DIRS: ['node_modules', '.git', '.yarn', 'dist', 'build', '.next', 'coverage'], // Directories to skip
}

// Function to count files in a directory without processing them
async function countFilesInDirectory(entry: AnyEntry, depth = 0): Promise<number> {
  const name = entry.name || ''

  // Skip if too deep
  if (depth > FOLDER_UPLOAD_LIMITS.MAX_DEPTH) {
    return 0
  }

  // Skip common build/cache directories
  if (FOLDER_UPLOAD_LIMITS.SKIP_DIRS.includes(name)) {
    return 0
  }

  if (entry.isDirectory) {
    const reader = entry.createReader!()
    const children = await readAllEntries(reader)

    // Limit the number of children processed
    const limitedChildren = children.slice(0, 100) // Process max 100 items per directory

    let totalFiles = 0

    for (const child of limitedChildren as AnyEntry[]) {
      // Check if we've hit the file limit
      if (totalFiles >= FOLDER_UPLOAD_LIMITS.MAX_FILES) {
        break
      }

      const childFiles = await countFilesInDirectory(child, depth + 1)

      totalFiles += childFiles
    }

    return totalFiles
  } else {
    // It's a file, count it
    return 1
  }
}

type DirTree = { name: string; children?: DirTree[] }

function setRelativePath(file: File, relPath: string) {
  try {
    Object.defineProperty(file, 'webkitRelativePath', { value: relPath, configurable: true })
  } catch (error) {
    console.error('Error setting relative path:', error)
  }
  ;(file as any).path = relPath
}

/** Walk a directory entry and return files (+ a pure directory tree). */
async function walkDirectory(
  entry: AnyEntry,
  relRoot: string,
  depth = 0,
): Promise<{ files: File[]; tree: DirTree }> {
  const name = entry.name || ''

  if (depth > FOLDER_UPLOAD_LIMITS.MAX_DEPTH) {
    return { files: [], tree: { name, children: [] } }
  }
  if (FOLDER_UPLOAD_LIMITS.SKIP_DIRS.includes(name)) {
    return { files: [], tree: { name, children: [] } }
  }

  if (entry.isDirectory) {
    const reader = entry.createReader!()
    const children = (await readAllEntries(reader)) as AnyEntry[]

    const files: File[] = []
    const childTrees: DirTree[] = []

    for (const child of children) {
      if (files.length >= FOLDER_UPLOAD_LIMITS.MAX_FILES) break
      if (child.isDirectory) {
        const sub = await walkDirectory(child, `${relRoot}/${child.name}`, depth + 1)

        childTrees.push(sub.tree)
        files.push(...sub.files)
      } else {
        const file: File = await new Promise((resolve, reject) =>
          (child as AnyEntry).file!(resolve, reject),
        )

        if (shouldFilterFile(file.name)) continue
        const relPath = `${relRoot}/${file.name}`

        setRelativePath(file, relPath)
        files.push(file)
      }
    }

    return { files, tree: { name, children: childTrees } }
  } else {
    const file: File = await new Promise((resolve, reject) => entry.file!(resolve, reject))

    if (shouldFilterFile(file.name)) return { files: [], tree: { name, children: [] } }
    setRelativePath(file, `${relRoot}`)

    return { files: [file], tree: { name, children: [] } }
  }
}

async function createFolderStructureFromDragDrop(
  files: (File & { path?: string })[],
  rootName: string,
  currentFolderId: string | undefined,
  parentPath: string,
  queryClient: any,
  treeOverride?: { name: string; children?: any[] },
): Promise<{ init: any; files: (File & { path?: string })[] } | null> {
  const tree =
    treeOverride ??
    (() => {
      const t: { name: string; children?: any[] } = { name: rootName, children: [] }
      const pathToNode = new Map<string, any>([[rootName, t]])

      for (const file of files) {
        const p = (file as any).path || (file as any).webkitRelativePath || file.name
        const parts = String(p).split('/').filter(Boolean)

        if (parts[0] === rootName) parts.shift()
        let cur = rootName
        let curNode = t

        for (const part of parts.slice(0, -1)) {
          cur = `${cur}/${part}`
          if (!pathToNode.has(cur)) {
            const newNode = { name: part, children: [] }

            curNode.children = curNode.children || []
            curNode.children.push(newNode)
            pathToNode.set(cur, newNode)
          }
          curNode = pathToNode.get(cur)
        }
      }

      return t
    })()

  const picked = { rootName, files, tree }

  const { result } = await initiateFolderTreeWithTracking({
    currentFolderId,
    parentPath,
    picked,
    queryClient,
  })

  // Check if quota was exceeded
  if (result && 'quotaExceeded' in result) {
    return null
  }

  return result
}

interface FileExplorerEmptyStateProps {
  emptyStateConfig: {
    icon: React.ReactNode
    title: string
    description?: string
  }
  uploadEnabled?: boolean
  parentId?: string
  searchParams: any
  parentType?: 'CHAT_ROOT' | 'CHAT_CONVERSATION'
}

const FileExplorerEmptyState: React.FC<FileExplorerEmptyStateProps> = ({
  emptyStateConfig,
  uploadEnabled = true,
  parentId,
  searchParams,
  parentType,
}) => {
  const [isDragActive, setIsDragActive] = useState(false)
  const dropRef = useRef<HTMLDivElement>(null)
  const { addFiles } = useProgressTrackerAdapter()
  const queryClient = useQueryClient()

  // Drag event handlers for the empty state dropzone
  const handleEmptyDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!uploadEnabled) return
    // Prevent drag and drop in system folders
    if (parentType) return
    setIsDragActive(true)
  }

  const handleEmptyDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only reset if leaving the dropzone
    if (!dropRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragActive(false)
    }
  }

  const handleEmptyDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)

    if (!uploadEnabled) {
      return
    }

    // Prevent drag and drop in system folders
    if (parentType) {
      return
    }

    const dt = e.dataTransfer
    const items = dt.items ? Array.from(dt.items) : []
    const collectedFiles: (File & { path?: string })[] = []
    const folders: Array<{
      name: string
      files: File[]
      fileCount: number
      totalSize: number
      tree: DirTree
    }> = []
    const looseFiles: File[] = []
    let hasDirectory = false

    if (items.length) {
      const directoryEntries: AnyEntry[] = []
      const fileEntries: File[] = []

      for (const it of items.filter(item => item.kind === 'file')) {
        const entry = (it as any).webkitGetAsEntry?.() as AnyEntry | undefined

        if (entry) {
          if (entry.isDirectory) {
            hasDirectory = true
            directoryEntries.push(entry)
          } else {
            const f = it.getAsFile()

            if (f && !shouldFilterFile(f.name)) fileEntries.push(f)
          }
        } else {
          const f = it.getAsFile()

          if (f && !shouldFilterFile(f.name)) fileEntries.push(f)
        }
      }

      for (const entry of directoryEntries) {
        try {
          const fileCount = await countFilesInDirectory(entry as any)

          if (fileCount > FOLDER_UPLOAD_LIMITS.MAX_FILES) {
            toast.error(
              `Cannot upload folder "${entry.name}". Folder contains more than ${FOLDER_UPLOAD_LIMITS.MAX_FILES} files.`,
            )
            continue
          }
          const { files: dirFiles, tree } = await walkDirectory(entry as any, entry.name)
          const totalSize = dirFiles.reduce((s, f) => s + f.size, 0)

          folders.push({ name: entry.name, files: dirFiles, fileCount, totalSize, tree })
          collectedFiles.push(...(dirFiles as any))
        } catch (error) {
          console.error('Error processing folder:', error)
        }
      }

      collectedFiles.push(...(fileEntries as any))
      looseFiles.push(...fileEntries)
    } else if (dt.files && dt.files.length) {
      const plain = Array.from(dt.files).filter(f => !shouldFilterFile(f.name))

      for (const f of plain) {
        if ((f as any).webkitRelativePath && (f as any).webkitRelativePath.includes('/')) {
          hasDirectory = true
        }
        collectedFiles.push(f as any)
      }
      looseFiles.push(...plain)
    }

    if (
      !hasDirectory &&
      collectedFiles.some(
        f => (f as any).webkitRelativePath && (f as any).webkitRelativePath.includes('/'),
      )
    ) {
      hasDirectory = true
    }

    // Multi-folder
    if (folders.length > 1) {
      const shouldProceed = await showMultiFolderModal(
        folders.map(f => ({
          name: f.name,
          files: f.files,
          fileCount: f.fileCount,
          totalSize: f.totalSize,
        })),
      )

      if (!shouldProceed) return
      for (const folder of folders) {
        try {
          const res = await createFolderStructureFromDragDrop(
            folder.files as any,
            folder.name,
            parentId,
            '/',
            queryClient,
            folder.tree as any,
          )

          if (res) {
            const { init, files: filesForFolder } = res

            addFiles(
              filesForFolder as any,
              parentId,
              [queryKeys.files.list(searchParams)],
              queryClient,
              '/',
              init,
            )
          }
        } catch (error) {
          console.error('Error processing folder:', error)
        }
      }
      if (looseFiles.length) {
        addFiles(
          looseFiles as any,
          parentId,
          [queryKeys.files.list(searchParams)],
          queryClient,
          '/',
        )
      }

      return
    }

    // Single folder (or mixed)
    if (hasDirectory && folders.length === 1) {
      const folder = folders[0]
      const res = await createFolderStructureFromDragDrop(
        folder.files as any,
        folder.name,
        parentId,
        '/',
        queryClient,
        folder.tree as any,
      )

      if (res) {
        const { init, files: filesForFolder } = res

        addFiles(
          filesForFolder as any,
          parentId,
          [queryKeys.files.list(searchParams)],
          queryClient,
          '/',
          init,
        )
      }
      if (looseFiles.length) {
        addFiles(
          looseFiles as any,
          parentId,
          [queryKeys.files.list(searchParams)],
          queryClient,
          '/',
        )
      }

      return
    }

    // No folders → plain files
    if (collectedFiles.length > 0) {
      addFiles(
        collectedFiles as any,
        parentId,
        [queryKeys.files.list(searchParams)],
        queryClient,
        '/',
      )
    }
  }

  return (
    <div
      className="flex flex-col items-center justify-center mx-4 md:mx-[30px] h-full"
      style={{ minHeight: '60vh' }}
    >
      <div
        ref={dropRef}
        style={{ borderRadius: 16 }}
        className={`relative w-full h-full mb-4 mx-auto justify-center flex flex-col items-center transition-all duration-200 ${
          isDragActive ? 'border-2 border-dashed border-primary bg-primary/10' : ''
        } rounded-2xl`}
        onDragLeave={handleEmptyDragLeave}
        onDragOver={handleEmptyDragOver}
        onDrop={handleEmptyDrop}
      >
        <EmptyState
          description={emptyStateConfig?.description || ''}
          icon={emptyStateConfig.icon}
          title={String(emptyStateConfig?.title || '')}
        />
      </div>
    </div>
  )
}

export default FileExplorerEmptyState
